import { composeGetTranslation } from '../../../../../../../common/react/utils/translations';
import { STATS_LEADERS_CATEGORY } from './categories';

export const FIRST_SORTABLE_COLUMN_INDEX = 4;

const STATS_LEADERS_COLUMN_IDS = {
    AFL_FANTASY_POINTS: 'dreamTeamPoints',
    GOALS: 'goals',
    GOAL_ASSIST: 'goalAssists',
    GOAL_ACCURACY: 'goalAccuracy',
    SHOTS_AT_GOAL: 'shotsAtGoal',
    BEHINDS: 'behinds',
    DISPOSALS: 'disposals',
    MARKS: 'marks',
    CONTESTED_MARKS: 'contestedMarks',
    MARKS_INSIDE_50: 'marksInside50',
    MARKS_ON_LEAD: 'marksOnLead',
    INTERCEPT_MARKS: 'interceptMarks',
    TACKLES: 'tackles',
    TACKLES_INSIDE_50: 'tacklesInside50',
    PRESSURE_ACTS: 'pressureActs',
    DEFENSIVE_HALF_PRESSURE_ACTS: 'defHalfPressureActs',
    CONTESTED_DEFENSIVE_ONE_ON_ONES: 'contestDefOneOnOnes',
    CONTESTED_DEFENSIVE_LOSSES: 'contestDefLosses',
    CONTESTED_DEFENSIVE_LOSS_PERCENT: 'contestDefLossPercentage',
    CLEARANCES: 'totalClearances',
    KICKS: 'kicks',
    HANDBALLS: 'handballs',
    HIT_OUTS: 'hitouts',
    FREES_FOR: 'freesFor',
    FREES_AGAINST: 'freesAgainst',
    BOUNCES: 'bounces',
    ONE_PERCENTERS: 'onePercenters',
    TIME_ON_GROUND: 'timeOnGroundPercentage',
    INSIDE_50S: 'inside50s',
    DISPOSAL_EFFICIENCY: 'disposalEfficiency',
    CLANGERS: 'clangers',
    REBOUND_50S: 'rebound50s',
    EFFECTIVE_KICKS: 'effectiveKicks',
    KICK_EFFICIENCY: 'kickEfficiency',
    KICK_TO_HANDBALL_RATIO: 'kickToHandballRatio',
    EFFECTIVE_DISPOSALS: 'effectiveDisposals',
    SPOILS: 'spoils',
    CONTESTED_POSSESSIONS: 'contestedPossessions',
    UNCONTESTED_POSSESSIONS: 'uncontestedPossessions',
    INTERCEPT_POSSESSIONS: 'intercepts',
    TURNOVERS: 'turnovers',
    CONTESTED_POSSESSION_RATE: 'contestedPossessionRate',
    GROUND_BALL_GETS: 'groundBallGets',
    FWD_50_GROUND_BALL_GETS: 'f50GroundBallGets',
    CENTRE_CLEARANCES: 'centreClearances',
    STOPPAGE_CLEARANCES: 'stoppageClearances',
    TOTAL_CLEARANCES: 'totalClearances',
    HITOUTS_TO_ADVANTAGE: 'hitoutsToAdvantage',
    HITOUTS_TO_ADVANTAGE_PERCENTAGE: 'hitoutToAdvantageRate',
    HITOUT_WIN_PERCENTAGE: 'hitoutWinPercentage',
    RUCK_CONTESTS: 'ruckContests',
    SCORE_INVOLVEMENTS: 'scoreInvolvements',
    SCORE_LAUNCHES: 'scoreLaunches',
};

/**
 * @typedef {object} Col
 * @property {string} id
 * @property {string} name
 * @property {string} abbreviation
 */

/**
 * @type {Object.<string, Col>}
 */
export const STATS_LEADERS_COLUMNS = {};

// eslint-disable-next-line guard-for-in
for ( let key in STATS_LEADERS_COLUMN_IDS ) {
    const id = STATS_LEADERS_COLUMN_IDS[ key ];
    const getTranslation = composeGetTranslation( `label.statsLeaders.${ id }.` );

    STATS_LEADERS_COLUMNS[ id ] = {
        id,
        name: getTranslation( 'name' ) ?? '',
        abbreviation: getTranslation( 'abbreviation' ) ?? ''
    };
}

Object.freeze( STATS_LEADERS_COLUMNS );
export const STATS_LEADERS_COLUMNS_FOR_CATEGORY = {
    [ STATS_LEADERS_CATEGORY.KEY_STATS ]: [
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.AFL_FANTASY_POINTS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.GOALS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.DISPOSALS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.MARKS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.TACKLES ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.CLEARANCES ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.KICKS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.HANDBALLS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.HIT_OUTS ],
    ],
    [ STATS_LEADERS_CATEGORY.GENERAL_PLAY ]: [
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.FREES_FOR ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.FREES_AGAINST ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.AFL_FANTASY_POINTS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.BOUNCES ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.ONE_PERCENTERS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.TIME_ON_GROUND ],
    ],
    [ STATS_LEADERS_CATEGORY.DISPOSALS ]: [
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.DISPOSALS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.KICKS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.HANDBALLS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.INSIDE_50S ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.DISPOSAL_EFFICIENCY ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.CLANGERS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.REBOUND_50S ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.EFFECTIVE_KICKS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.KICK_EFFICIENCY ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.KICK_TO_HANDBALL_RATIO ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.EFFECTIVE_DISPOSALS ],
    ],
    [ STATS_LEADERS_CATEGORY.POSSESSIONS ]: [
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.CONTESTED_POSSESSIONS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.UNCONTESTED_POSSESSIONS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.INTERCEPT_POSSESSIONS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.TURNOVERS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.CONTESTED_POSSESSION_RATE ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.GROUND_BALL_GETS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.FWD_50_GROUND_BALL_GETS ],
    ],
    [ STATS_LEADERS_CATEGORY.STOPPAGES ]: [
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.HIT_OUTS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.CENTRE_CLEARANCES ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.STOPPAGE_CLEARANCES ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.TOTAL_CLEARANCES ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.HITOUTS_TO_ADVANTAGE ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.HITOUT_WIN_PERCENTAGE ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.HITOUTS_TO_ADVANTAGE_PERCENTAGE ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.RUCK_CONTESTS ],
    ],
    [ STATS_LEADERS_CATEGORY.MARKS ]: [
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.MARKS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.INSIDE_50S ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.CONTESTED_MARKS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.MARKS_ON_LEAD ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.INTERCEPT_MARKS ],
    ],
    [ STATS_LEADERS_CATEGORY.SCORING ]: [
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.GOALS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.BEHINDS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.GOAL_ASSIST ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.GOAL_ACCURACY ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.SHOTS_AT_GOAL ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.SCORE_INVOLVEMENTS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.SCORE_LAUNCHES ],
    ],
    [ STATS_LEADERS_CATEGORY.DEFENCE ]: [
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.TACKLES ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.TACKLES_INSIDE_50 ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.PRESSURE_ACTS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.DEFENSIVE_HALF_PRESSURE_ACTS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.SPOILS ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.CONTESTED_DEFENSIVE_ONE_ON_ONES ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.CONTESTED_DEFENSIVE_LOSSES ],
        STATS_LEADERS_COLUMNS[ STATS_LEADERS_COLUMN_IDS.CONTESTED_DEFENSIVE_LOSS_PERCENT ],
    ],
};

Object.freeze( STATS_LEADERS_COLUMNS_FOR_CATEGORY );